import { useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ErrorSummary } from '@/components/forge/Field'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { Icon } from '@/components/forge/Icon'
import { Panel } from '@/components/forge/Panel'
import { Num } from '@/components/forge/primitives'
import { EmptyState, ErrorState, SkeletonRows } from '@/components/forge/States'
import {
  buildExtractionConfig,
  buildSparseConfig,
  buildWaveformMetadata,
  invalidFormat,
  mergedExtractionValues,
  missingRequired,
} from '@/features/dataFusion/api'
import { usePermission } from '@/features/auth/permissions'
import { useConnection, useConnectorTypes } from '@/features/dataFusion/queries'
import { useAssetClasses } from '@/features/assetClasses/queries'
import { usePlaceNodes } from '@/features/hierarchy/queries'
import { parentOf } from '@/features/hierarchy/parents'
import { DEFAULT_VALUE_TYPE, waveformTypeById } from '@/features/sensors/api'
import {
  useCreateSensor,
  useSensor,
  useUpdateSensor,
  useValueUnits,
  useWaveformTypes,
} from '@/features/sensors/queries'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useCopilotRecord } from '@/shell/AppShell'
import { SensorForm } from './SensorForm'
import { WriteFailed } from './SensorDialogs'
import { connectionPath } from './sections'

/**
 * Onboarding and editing a sensor, as a screen.
 *
 * ── Why this is not a dialog ──────────────────────────────────────────────
 * It was one, and the form did not fit: `ForgeDialog`'s body is capped at
 * `min(60vh, 32rem)`, and this form carries eight fields — three of them generated
 * from the connector's `extraction_schema` — plus a three-level placement cascade.
 * Everything past the first section lived below a scroll line, which means the
 * thing you are about to save cannot be read in one look before you save it.
 *
 * As a route it also gets what a dialog cannot have: an address someone can send,
 * a back target that survives a reload, and browser history — so the escape from a
 * half-filled form is Back rather than a discarded dialog.
 *
 * ── One component, two modes ──────────────────────────────────────────────
 * `:sensorId` present means edit. The form, its validation and its payload
 * assembly are identical either way; what differs is where the initial values come
 * from and which mutation runs. Two components would be two copies of the same
 * coercion rules to keep in step with one validator.
 */
export function SensorFormPage() {
  const { connectionId, sensorId } = useParams()
  const { t } = useTranslation(['app', 'common'])

  /**
   * This screen needs BOTH permissions, which is unusual and is the API's shape.
   *
   * The write is `sensors.manage_sensors`. But the form generates itself from the
   * connector type's `extraction_schema`, and both routes that can supply it —
   * the connection detail read and the catalogue — are behind
   * `sensors.manage_connections`. Without it there is no field list, so there is
   * no form: `validate_config` would reject whatever a blank one assembled, and a
   * screen that rendered anyway would be a set of controls that cannot be saved.
   *
   * Both reads are disabled rather than merely unused, so neither fires a request
   * that would 403.
   */
  const manageSensors = usePermission('sensors.manage_sensors')
  const manageConnections = usePermission('sensors.manage_connections')
  const gatePending = manageSensors.isPending || manageConnections.isPending
  const canRead = manageConnections.granted

  const connection = useConnection(canRead ? connectionId : null)
  const catalogue = useConnectorTypes({ enabled: canRead })
  const assetClasses = useAssetClasses()
  const waveformTypes = useWaveformTypes({ enabled: manageSensors.granted })
  const valueUnits = useValueUnits({ enabled: manageSensors.granted })
  const sensor = useSensor(manageSensors.granted ? sensorId : null)
  // The sensor's name in the assistant's context chip, when editing one.
  useCopilotRecord('sensors', sensor.data?.id, sensor.data?.name)
  /* The two places the form names: the sensor's own parent on an edit, and the
     connection's site, which a new device defaults to. Read through the
     per-level detail endpoints — see `usePlaceNodes`. Still in the `pending` gate
     below, and correctly so: the targets are known in the same render the
     sensor and connection land, so the form never mounts before its names. */
  const placeTargets = useMemo(
    () => [
      parentOf(sensor.data),
      connection.data?.plantId ? { tier: 'site', id: connection.data.plantId } : null,
    ],
    [sensor.data, connection.data],
  )
  const hierarchy = usePlaceNodes(placeTargets)

  const editing = Boolean(sensorId)

  useDocumentTitle(
    `${editing ? t('dataFusion.sensors.edit.title') : t('dataFusion.sensors.create.title')} · ${t('common:brand.name')}`,
  )

  const connector = useMemo(() => {
    if (connection.data?.connectorType) return connection.data.connectorType
    return (
      (catalogue.data?.items ?? []).find((type) => type.id === connection.data?.connectorTypeId) ??
      null
    )
  }, [connection.data, catalogue.data])

  /**
   * Nothing renders until every source has landed.
   *
   * The form's values are built in a `useState` initialiser, which runs exactly
   * once — mounting before the sensor or the schema arrives would initialise blank
   * and never prefill, and a full-replace PUT built from that sends empty strings
   * over stored values. `isPending` is false on failure too, so a failed request
   * falls through to the error branch rather than holding a skeleton forever.
   *
   * The waveform catalogue belongs in this list and not merely in it for tidiness.
   * `waveform_metadata` is assembled from the SELECTED type's `metadata_schema`,
   * so a form that mounted before the catalogue arrived would build it from an
   * empty field list — sending `{}` and, on an edit, replacing a stored alarm band
   * with nothing while returning 200.
   */
  const pending =
    gatePending ||
    connection.isPending ||
    catalogue.isPending ||
    hierarchy.isPending ||
    assetClasses.isPending ||
    waveformTypes.isPending ||
    valueUnits.isPending ||
    (editing && sensor.isPending)

  /**
   * The master data is a hard dependency, so a failure to load it is fatal rather
   * than degradable. `waveform_type_id` and `value_unit_id` are non-nullable with
   * no server-side default: without the lists there is nothing to choose from and
   * nothing to fall back on, so every submit would 422. Better to say the screen
   * could not load and offer a retry than to render a form that cannot be saved.
   */
  const failed =
    connection.isError || waveformTypes.isError || valueUnits.isError || (editing && sensor.isError)

  return (
    <div className="px-8 py-6 max-xl:px-5.5">
      <Link
        to={connectionPath(connectionId)}
        className="inline-flex items-center gap-1.5 text-body-sm text-ink-dim transition-colors hover:text-ink"
      >
        <Icon name="right" size="xs" className="rotate-180 rtl:rotate-0" />
        {connection.data?.name ?? t('dataFusion.sensors.backToSensors')}
      </Link>

      {gatePending ? (
        <div className="mt-5">
          <SkeletonRows count={4} />
        </div>
      ) : !manageSensors.granted || !manageConnections.granted ? (
        /* Both, and the body says which is missing rather than naming both at
           somebody who holds one — "ask for a permission you already have" is the
           kind of instruction that sends a person to the wrong place. */
        <EmptyState
          icon="lock"
          title={t('dataFusion.sensors.formForbidden')}
          body={t(
            manageSensors.granted
              ? 'dataFusion.sensors.formForbiddenConnections'
              : 'dataFusion.sensors.formForbiddenSensors',
          )}
          className="mt-5"
        />
      ) : pending ? (
        <div className="mt-5">
          <SkeletonRows count={4} />
        </div>
      ) : failed ? (
        <ErrorState
          error={connection.error ?? sensor.error ?? waveformTypes.error ?? valueUnits.error}
          title={t(
            editing ? 'dataFusion.sensors.edit.loadFailed' : 'dataFusion.sensors.connectionFailed',
          )}
          onRetry={() => {
            connection.refetch()
            waveformTypes.refetch()
            valueUnits.refetch()
            if (editing) sensor.refetch()
          }}
          className="mt-5"
        />
      ) : (
        <Editor
          key={sensorId ?? 'new'}
          connection={connection.data}
          connector={connector}
          hierarchy={hierarchy}
          assetClasses={assetClasses}
          waveformTypes={waveformTypes.data?.items ?? []}
          valueUnits={valueUnits.data?.items ?? []}
          sensor={editing ? sensor.data : null}
        />
      )}
    </div>
  )
}

/* ══ THE FORM ══════════════════════════════════════════════════════════ */

function Editor({
  connection,
  connector,
  hierarchy,
  assetClasses,
  waveformTypes,
  valueUnits,
  sensor,
}) {
  const { t } = useTranslation(['app', 'common'])
  const navigate = useNavigate()

  const create = useCreateSensor()
  const update = useUpdateSensor()

  const editing = Boolean(sensor)
  const write = editing ? update : create
  const extractionFields = connector?.extractionFields ?? []

  const summaryRef = useRef(null)
  const [values, setValues] = useState(() =>
    initialValues({ extractionFields, waveformTypes, sensor, connection }),
  )
  const [showErrors, setShowErrors] = useState(false)
  /** The last sensor onboarded, held so the run can continue without a reload. */
  const [created, setCreated] = useState(null)

  const errors = formErrors({
    values,
    extractionFields,
    waveformTypes,
    serverFields: write.error?.fields ?? {},
    t,
  })
  const blocked = isBlocked(errors)

  /* ── State updates, one per level ─────────────────────────────────────
     A tag is replaced rather than mutated, and the array with it: React state is
     compared by identity, and editing a signal in place would leave the panel
     showing the old value until something else forced a render. */
  const patchTag = (index, patch) =>
    setValues((current) => ({
      ...current,
      tags: current.tags.map((tag, at) => (at === index ? { ...tag, ...patch } : tag)),
    }))

  const patchTagMap = (index, mapKey, key, value) =>
    setValues((current) => ({
      ...current,
      tags: current.tags.map((tag, at) =>
        at === index ? { ...tag, [mapKey]: { ...tag[mapKey], [key]: value } } : tag,
      ),
    }))

  /**
   * A new signal, seeded from the one above it.
   *
   * Everything except the name, the description and the extraction overrides —
   * the same reasoning "Onboard another" uses one function down. A device with
   * eight thermocouples has eight identical ranges, units and shapes, and
   * re-typing them is eight chances to fat-finger one. The paths are what differ,
   * so they start blank and the form asks for them.
   */
  function addTag() {
    setValues((current) => {
      const last = current.tags.at(-1)
      return {
        ...current,
        tags: [
          ...current.tags,
          {
            ...blankTag(extractionFields),
            valueType: last?.valueType ?? DEFAULT_VALUE_TYPE,
            valueUnitId: last?.valueUnitId ?? '',
            waveformTypeId: last?.waveformTypeId ?? '',
            waveformMetadata: { ...(last?.waveformMetadata ?? {}) },
          },
        ],
      }
    })
  }

  /**
   * Drop a signal.
   *
   * On an edit this is a DELETION and not a removal from a form: `_replace_tags`
   * soft-deletes every stored tag whose id is not in the payload, and a tag id is
   * a QuestDB partition key that is never reissued. So the id simply leaves the
   * list and the save carries out the intent — which is why the confirmation for
   * it is the save button rather than a dialog per row.
   */
  const removeTag = (index) =>
    setValues((current) => ({
      ...current,
      tags: current.tags.filter((_, at) => at !== index),
    }))

  function submit() {
    setShowErrors(true)
    if (blocked) {
      // Focused rather than merely rendered: a keyboard user who submits an invalid
      // form otherwise gets no signal that anything happened.
      summaryRef.current?.focus()
      return
    }

    const tags = values.tags.map((tag) => {
      // Built from the SELECTED type's fields only. State holds every value ever
      // typed, including an `update_interval` left behind by a shape that wanted
      // one — assembling from this list is what keeps that out of the request,
      // where the validator would have no field to match it against.
      const metadataFields = waveformTypeById(waveformTypes, tag.waveformTypeId)?.metadataFields ?? []
      return {
        id: tag.id,
        name: tag.name,
        description: tag.description,
        valueType: tag.valueType,
        // Sparse, unlike every other payload in this app: a key present here
        // REPLACES the device's rather than falling back to it. See
        // `buildSparseConfig`.
        extractionConfig: buildSparseConfig(extractionFields, tag.extraction),
        waveformTypeId: tag.waveformTypeId,
        waveformMetadata: buildWaveformMetadata(metadataFields, tag.waveformMetadata),
        valueUnitId: tag.valueUnitId,
      }
    })

    const common = {
      name: values.name,
      description: values.description,
      assetClassId: values.assetClassId,
      // The device's half is complete — every declared key, blanks as null. It is
      // what each tag's sparse config is merged OVER, so a gap here is a gap in
      // the merged view for every signal that did not fill it itself.
      extractionConfig: buildExtractionConfig(extractionFields, values.extraction),
      tags,
    }

    if (editing) {
      // `parent` and the connection are deliberately not spread through:
      // SensorUpdate accepts neither, and Pydantic would drop them without
      // complaint. See updateSensor.
      update.mutate({ id: sensor.id, ...common }, { onSuccess: () => back() })
    } else {
      create.mutate(
        { ...common, parent: values.parent, connectionId: connection.id },
        { onSuccess: (row) => setCreated(row) },
      )
    }
  }

  const back = () => navigate(connectionPath(connection.id))

  /**
   * Go again, keeping what a run of devices holds constant.
   *
   * The name, the description and the extraction paths change per device; the
   * placement, the class and the shape of the signals usually do not. Onboarding
   * forty thermocouples off one line means forty identical ranges and alarm
   * bands, so re-typing them is forty times the same four numbers — which is also
   * forty chances to fat-finger one.
   *
   * The signals are carried over with their ids STRIPPED. A tag id belongs to the
   * device it was created under; carrying one into the next create would send the
   * server a key it has no field for on `SensorTagCreate`, and on the following
   * edit it would name a tag on somebody else's row.
   */
  function another() {
    setCreated(null)
    create.reset()
    setShowErrors(false)
    setValues((current) => ({
      ...initialValues({ extractionFields, waveformTypes, connection }),
      parent: current.parent,
      assetClassId: current.assetClassId,
      tags: current.tags.map((tag) => ({
        ...tag,
        key: newKey(),
        id: null,
        name: '',
        description: '',
        extraction: blankMap(extractionFields),
      })),
    }))
  }

  return (
    <>
      <header className="mt-3 mb-5">
        <h1 className="m-0 text-headline">
          {editing
            ? t('dataFusion.sensors.edit.heading', { name: sensor.name })
            : t('dataFusion.sensors.create.title')}
        </h1>
        {/* The intro is instructions for filling a form. Once one is onboarded the
            form is gone, and so is the reason to explain it. */}
        {created ? null : (
          <p className="mt-2 mb-0 max-w-[72ch] text-body-sm text-ink-dim">
            {t(editing ? 'dataFusion.sensors.edit.intro' : 'dataFusion.sensors.create.intro')}
          </p>
        )}
      </header>

      {/* ── The confirmation REPLACES the form, it does not sit above it ─────
          Leaving the filled form on screen behind the success panel reads as a
          sensor that has not been saved yet — the fields still hold the values
          that were just written, so the page shows a record and a draft of the
          same record at once, and the sticky Onboard button below invites writing
          it twice. There is one action after a write, and it is choosing what to
          do next. */}
      {created ? (
        <Onboarded sensor={created} connector={connector} onAnother={another} onDone={back} />
      ) : (
        <>
          <div className="flex flex-col gap-4">
            {showErrors && blocked ? (
              <ErrorSummary
                ref={summaryRef}
                title={t('dataFusion.sensors.fields.fixFirst')}
                errors={summaryAnchors(errors)}
                className="max-w-[72ch]"
              />
            ) : null}

            {write.error ? (
              <WriteFailed
                error={write.error}
                action={editing ? 'update' : 'create'}
                className="max-w-[72ch]"
              />
            ) : null}

            <SensorForm
              connection={connection}
              connector={connector}
              hierarchy={hierarchy}
              assetClasses={assetClasses}
              waveformTypes={waveformTypes}
              valueUnits={valueUnits}
              values={values}
              errors={showErrors ? errors : BLANK_ERRORS}
              onChange={(patch) => setValues((current) => ({ ...current, ...patch }))}
              parentLocked={editing}
              onExtractionChange={(key, value) =>
                setValues((current) => ({
                  ...current,
                  extraction: { ...current.extraction, [key]: value },
                }))
              }
              onTagChange={patchTag}
              onTagExtractionChange={(index, key, value) =>
                patchTagMap(index, 'extraction', key, value)
              }
              onTagMetadataChange={(index, key, value) =>
                patchTagMap(index, 'waveformMetadata', key, value)
              }
              onAddTag={addTag}
              onRemoveTag={removeTag}
            />
          </div>

          {/* Pinned to the bottom of the viewport rather than the end of the form,
              so the action stays reachable on a narrow window where the two columns
              have stacked. It is the page's only primary. */}
          <div className="sticky bottom-0 z-10 mt-6 flex flex-wrap gap-2 border-t border-hairline bg-page py-3.5">
            <ForgeButton variant="primary" size="lg" disabled={write.isPending} onClick={submit}>
              {write.isPending
                ? t(editing ? 'dataFusion.sensors.edit.saving' : 'dataFusion.sensors.create.saving')
                : t(editing ? 'dataFusion.sensors.edit.save' : 'dataFusion.sensors.create.submit')}
            </ForgeButton>
            <ForgeButton size="lg" disabled={write.isPending} onClick={back}>
              {t('common:actions.cancel')}
            </ForgeButton>
          </div>
        </>
      )}
    </>
  )
}

/**
 * The sensor exists, and the run can continue.
 *
 * Named rather than a bare confirmation, because onboarding happens in dozens and a
 * list of what you just created is the only audit trail during a run. "Onboard
 * another" keeps the placement and the signal shapes — what a run holds constant
 * while the name and the paths change.
 */
function Onboarded({ sensor, connector, onAnother, onDone }) {
  const { t } = useTranslation(['app', 'common'])

  return (
    <Panel className="mb-5 max-w-[72ch] stripe-green">
      <div className="flex flex-wrap items-start gap-3">
        <Icon name="check" size="lg" className="mt-0.5 shrink-0 text-on-green" />
        <div className="min-w-0 flex-1">
          <p className="m-0 text-body font-semibold text-ink">{sensor.name}</p>
          <p className="mt-1 mb-0 text-body-sm text-ink-dim">
            {/* Counted off the SERVER's answer, not the form's. The create returns
                the tags it actually stored, and a count taken from the draft would
                report what was asked for rather than what exists. */}
            <Num>{t('dataFusion.sensors.create.signalCount', { count: sensor.tags.length })}</Num>
            {connector ? <span translate="no">{` · ${connector.name}`}</span> : null}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <ForgeButton onClick={onAnother}>{t('dataFusion.sensors.create.another')}</ForgeButton>
            <ForgeButton onClick={onDone}>{t('dataFusion.sensors.create.done')}</ForgeButton>
          </div>
        </div>
      </div>
    </Panel>
  )
}

/* ══ FORM STATE ════════════════════════════════════════════════════════ */

/**
 * A stable React key for a signal panel.
 *
 * The array index cannot serve: removing signal two would give signal three the
 * key that signal two had, and React would keep the removed panel's DOM — with
 * its scroll position, its focus and its open builder — under the new row. A
 * stored tag's own id cannot serve either, because a new one has none until it is
 * saved.
 */
let keySeed = 0
const newKey = () => `tag-${(keySeed += 1)}`

/** Every declared key present and blank, which is what keeps an input controlled. */
function blankMap(fields) {
  const values = {}
  for (const field of fields) values[field.key] = field.type === 'checkbox' ? false : ''
  return values
}

/**
 * A fresh signal.
 *
 * Neither the shape nor the unit is defaulted to its list's first entry, which
 * would be the tempting thing to do with a required select. There is no
 * defensible default: the catalogue is seeded in insertion order, so "first"
 * means Sinusoidal and Celsius for reasons that have nothing to do with the
 * sensor being onboarded — and a pre-filled wrong answer is worse than an empty
 * required one, because the empty one gets looked at.
 */
function blankTag(extractionFields) {
  return {
    key: newKey(),
    id: null,
    name: '',
    description: '',
    valueType: DEFAULT_VALUE_TYPE,
    extraction: blankMap(extractionFields),
    waveformTypeId: '',
    waveformMetadata: {},
    valueUnitId: '',
  }
}

/**
 * Blank values for a fresh device, or the stored ones re-read for editing.
 *
 * A new device's placement is seeded from the connection's own site — the record
 * has already answered that question, so asking it again is a click nobody owes.
 *
 * A fresh device starts with exactly one signal, because `tags` is `min_length=1`
 * and a form that opened with none would make the first action "add the thing
 * this form is for".
 */
function initialValues({ extractionFields, waveformTypes, sensor, connection }) {
  return {
    name: sensor?.name ?? '',
    description: sensor?.description ?? '',
    parent: sensor
      ? parentOf(sensor)
      : connection?.plantId
        ? { tier: 'site', id: connection.plantId }
        : null,
    assetClassId: sensor?.assetClassId ?? '',
    extraction: prefill(extractionFields, sensor?.extractionConfig),
    tags: sensor?.tags?.length
      ? sensor.tags.map((tag) => storedTag(tag, { extractionFields, waveformTypes }))
      : [blankTag(extractionFields)],
  }
}

/**
 * One stored tag, re-read into form state.
 *
 * Waveform metadata is prefilled from the STORED type's schema, not a selected
 * one: resolving the id is what keeps a Sinusoidal tag's range from being read
 * through a Constant's field list, left unread, and then overwritten with nothing
 * on save. See `waveformTypeById` for why an unresolvable id is a real state.
 */
function storedTag(tag, { extractionFields, waveformTypes }) {
  const stored = waveformTypeById(waveformTypes, tag.waveformTypeId)
  return {
    key: newKey(),
    id: tag.id,
    name: tag.name ?? '',
    description: tag.description ?? '',
    valueType: tag.valueType ?? DEFAULT_VALUE_TYPE,
    extraction: prefill(extractionFields, tag.extractionConfig),
    waveformTypeId: tag.waveformTypeId ?? '',
    waveformMetadata: prefill(stored?.metadataFields ?? [], tag.waveformMetadata),
    valueUnitId: tag.valueUnitId ?? '',
  }
}

/**
 * A generated field set's starting values, flat and keyed by dotted schema key.
 *
 * Flat here and nested on the wire: `setPath` in `buildConnectionConfig` does the
 * conversion on the way out and `readPath` does it on the way in, so the form
 * state in between is one level deep and a control binds to `values[field.key]`
 * without walking anything.
 *
 * Shared by both generated sections because the rules are identical — a checkbox
 * coerces to a boolean so an absent stored value cannot make it `undefined` and
 * turn the input uncontrolled, and everything else starts as `''` for the same
 * reason.
 */
function prefill(fields, storedConfig) {
  const values = {}
  for (const field of fields) {
    const stored = storedConfig ? readPath(storedConfig, field.key) : undefined
    values[field.key] = field.type === 'checkbox' ? Boolean(stored) : (stored ?? '')
  }
  return values
}

/** Reads a dotted schema key out of a stored config, which may be nested. */
function readPath(config, key) {
  if (!config) return undefined
  return key.split('.').reduce((node, part) => (node == null ? undefined : node[part]), config)
}

/* ══ VALIDATION ════════════════════════════════════════════════════════ */

/** Nothing reported yet — the shape the form reads before the first submit. */
const BLANK_ERRORS = { extraction: {}, tagErrors: [] }

/**
 * Everything wrong with the form right now, keyed the way the controls are.
 *
 * Required-and-blank is checked here because this is where the offending control
 * is on screen to be pointed at. Nothing else is: `validate_config` is the
 * authority on types and select options, and the parent's account ownership is a
 * fact only the server holds — reimplementing either would be a copy that drifts
 * from the validator it mirrors.
 *
 * Three rules are checked locally because they must be. Exactly-one-parent,
 * `description`'s `min_length=1` and `tags`'s `min_length=1` are all Pydantic
 * constraints, so violating any of them returns FastAPI's 422 shape rather than
 * the field-keyed envelope — and `apiError.js` flattens that to the key `body`,
 * which names no control.
 *
 * ── The extraction check is on the MERGE, not on either half ──────────────
 * `_validate_tag_extraction_config` validates `{**device, **tag}` per tag, so
 * neither half is required to be complete on its own and checking them separately
 * would report a missing `topic` on every signal of a device that supplies one.
 * The result is attributed to the SIGNAL rather than to the device, because that
 * is the level at which it is actually wrong: a device with one signal that fills
 * every field is valid however empty the shared panel looks.
 */
function formErrors({ values, extractionFields, waveformTypes, serverFields, t }) {
  const errors = { extraction: {}, tagErrors: [] }

  if (!values.name.trim()) errors.name = t('dataFusion.sensors.fields.nameRequired')
  if (!values.description.trim()) {
    errors.description = t('dataFusion.sensors.fields.descriptionRequired')
  }
  if (!values.parent?.id) errors.parent = t('dataFusion.sensors.fields.parentRequired')
  if (!values.tags.length) errors.tags = t('dataFusion.sensors.fields.signalRequired')

  // Format is checked on the device's own values wherever it carries one. Blanks
  // are skipped by `invalidFormat`, which is correct here: a blank shared field is
  // a field the signals answer themselves.
  for (const key of Object.keys(invalidFormat(extractionFields, values.extraction))) {
    errors.extraction[key] = t('dataFusion.sensors.fields.extractionFormat')
  }

  errors.tagErrors = values.tags.map((tag) => {
    const tagErrors = { extraction: {}, waveformMetadata: {} }

    // Both are non-nullable UUIDs with no default, and both would come back as a
    // Pydantic 422 rather than a field error — same reasoning as `description`.
    if (!tag.waveformTypeId) {
      tagErrors.waveformTypeId = t('dataFusion.sensors.fields.waveformTypeRequired')
    }
    if (!tag.valueUnitId) tagErrors.valueUnitId = t('dataFusion.sensors.fields.valueUnitRequired')

    const merged = mergedExtractionValues(extractionFields, values.extraction, tag.extraction)
    for (const key of Object.keys(missingRequired(extractionFields, merged))) {
      tagErrors.extraction[key] = t('dataFusion.sensors.fields.extractionRequired')
    }
    // Only this signal's own overrides are format-checked. A malformed shared value
    // is reported once on the device panel above rather than once per signal.
    for (const key of Object.keys(invalidFormat(extractionFields, tag.extraction))) {
      tagErrors.extraction[key] = t('dataFusion.sensors.fields.extractionFormat')
    }

    /**
     * The same two checks over the waveform type's fields, and this is the
     * SELECTED type's list — so nothing is demanded for a shape that is not
     * chosen. When no type is selected the list is empty, which is correct: the
     * unanswered question is the type itself, reported above, and demanding a
     * range for a shape nobody has picked would be two errors for one omission.
     *
     * `missingRequired` coerces before testing, so a `number` field holding the
     * unparseable "12.5.3" reports as missing rather than passing through to a
     * server-side type error. Every field in every seeded `metadata_schema` is a
     * required number, which makes that the common path rather than an edge.
     */
    const metadataFields = waveformTypeById(waveformTypes, tag.waveformTypeId)?.metadataFields ?? []
    for (const key of Object.keys(missingRequired(metadataFields, tag.waveformMetadata))) {
      tagErrors.waveformMetadata[key] = t('dataFusion.sensors.fields.metadataRequired')
    }
    for (const key of Object.keys(invalidFormat(metadataFields, tag.waveformMetadata))) {
      tagErrors.waveformMetadata[key] = t('dataFusion.sensors.fields.metadataFormat')
    }

    return tagErrors
  })

  /**
   * The server's own field-keyed `reason`, merged onto whichever section declares
   * the key.
   *
   * `ValidationAppError` carries a flat `dict[str, str]` with no indication of
   * which schema rejected or which tag it was validating — only the message prefix
   * ("Extraction config is invalid." versus "Waveform metadata is invalid.")
   * distinguishes the first, and matching on English prose from a server would be
   * worse than the ambiguity. So a key is matched against the field list that
   * declares it and shown on EVERY signal: the alternative is showing it on none,
   * and a rejection nobody can locate is a save that fails for no stated reason.
   * Recorded in BACKEND-GAPS.md §7 — a tag index on the error key is the whole fix.
   */
  if (serverFields.name) errors.name = t('dataFusion.sensors.fields.nameRejected')
  if (serverFields.tags) errors.tags = t('dataFusion.sensors.fields.tagsRejected')

  for (const tagErrors of errors.tagErrors) {
    for (const field of extractionFields) {
      if (serverFields[field.key]) {
        tagErrors.extraction[field.key] = t('dataFusion.sensors.fields.extractionRejected')
      }
    }
  }
  for (const [index, tag] of values.tags.entries()) {
    const metadataFields = waveformTypeById(waveformTypes, tag.waveformTypeId)?.metadataFields ?? []
    for (const field of metadataFields) {
      if (serverFields[field.key]) {
        errors.tagErrors[index].waveformMetadata[field.key] = t(
          'dataFusion.sensors.fields.metadataRejected',
        )
      }
    }
  }

  return errors
}

/** Whether anything at all is wrong, device or signal. */
function isBlocked(errors) {
  if (
    errors.name ||
    errors.description ||
    errors.parent ||
    errors.tags ||
    Object.keys(errors.extraction).length
  ) {
    return true
  }
  return errors.tagErrors.some(
    (tag) =>
      tag.name ||
      tag.description ||
      tag.waveformTypeId ||
      tag.valueUnitId ||
      Object.keys(tag.extraction).length ||
      Object.keys(tag.waveformMetadata).length,
  )
}

/**
 * Field-keyed errors → the DOM ids `ErrorSummary` links to.
 *
 * Every generated field set on this screen has its own key space and its own id
 * prefix — the device's extraction, and per signal an extraction set and a
 * metadata set. `ConfigField` namespaces the ids for exactly that reason, and
 * these anchors have to spell the same prefixes or every link silently points at
 * nothing. The dot substitution has to match its own too.
 */
function summaryAnchors(errors) {
  const anchors = {
    'sensor-name': errors.name,
    'sensor-description': errors.description,
    'sensor-site': errors.parent,
    ...anchored('cfg', errors.extraction),
  }

  for (const [index, tag] of errors.tagErrors.entries()) {
    anchors[`tag${index}-name`] = tag.name
    anchors[`tag${index}-description`] = tag.description
    anchors[`tag${index}-waveform-type`] = tag.waveformTypeId
    anchors[`tag${index}-value-unit`] = tag.valueUnitId
    Object.assign(anchors, anchored(`tag${index}-cfg`, tag.extraction))
    Object.assign(anchors, anchored(`tag${index}-wf`, tag.waveformMetadata))
  }

  return anchors
}

function anchored(prefix, fieldErrors) {
  return Object.fromEntries(
    Object.entries(fieldErrors).map(([key, message]) => [
      `${prefix}-${key.replace(/\./g, '-')}`,
      message,
    ]),
  )
}
